<template>
  <div>
    <v-data-table
      v-model:expanded="expanded"
      :loading="loading"
      :headers="customHeaders.filter((h) => !h.hidden)"
      :items="tariffs"
      :hide-default-footer="true"
      :single-expand="true"
      item-key="tariffCode"
      :show-expand="isOfferTariff"
      disable-pagination
    >
      <template #expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <div class="d-flex flex-wrap col-12 justify-between">
            <div
              v-for="[key, val] in Object.entries(fields).filter(([k,v]) => !v.show || v.show(item))"
              :key="item.tariffCode+key"
              :class="`d-flex col-${val.col || 12}`"
            >
              <PickerExternal
                v-if="val.viewType === 'PickerExternal'"
                v-model="item[key]"
                class="col-12"
                :field="val"
                :content="item"
                :filters="[{ field: 'codeProduits', value: [item.horanetProductCode] }]"
                @input="updateField($event, item, key)"
              />
              <SimpleField
                v-if="isSimpleField(val)"
                v-model="item[key]"
                class="d-flex col-12"
                :field="val"
                @input="updateField($event, item, key)"
              />
            </div>
          </div>
        </td>
      </template>
      <template #[`item.selected`]="{ item }">
        <v-switch
          class="ma-0"
          :input-value="
            ((selectedTarifs || []).find((t) => t.codeTariff === item.tariffCode))?.isActive || false
          "
          color="green"
          hide-details
          @change="updateField($event, item, 'isActive')"
        />
      </template>
      <template v-if="isOfferTariff" #footer>
        <div class="flex v-data-footer pa-4 justify-end">
          <v-btn color="red lighten-1 mr-1" @click="clearAllTarifs">
            Clear
          </v-btn>
          <v-btn color="primary" @click="selectAllTarifs">Select All</v-btn>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineProps,
  ref,
  onBeforeMount,
  watch
} from 'vue';

import { useLazyQuery } from '@vue/apollo-composable';
import { useStore } from 'vue2-helpers/vuex';
import { getGraphqlSchema } from '../../../graphql/index';

import SimpleField from '@/components/editor/SimpleField.vue';
import PickerExternal from '@/components/editor/PickerExternal.vue';

const store = useStore();
const emit = defineEmits(['input']);
const props = defineProps({
  value: {
    type: Array,
    default: () => []
  },
  horanetProduct: {
    type: Object,
    default: () => null
  },
  field: {
    type: Object,
    default: () => null
  },
  rawContentTypes: {
    type: Object,
    default: () => null
  }
});

// eslint-disable-next-line vue/no-setup-props-destructure
const { horanetProduct, field } = props;

const expanded = ref([]);
const descriptions = ref({});
const selectedTarifs = ref(props.value || []);
const enabled = computed(
  () => horanetProduct
    && horanetProduct.horanetProductCode
    && horanetProduct.horanetProductCode !== ''
);
const isOfferTariff = computed(
  () => field.type === 'AlphaSelectedHoranetTariff'
);
const customHeaders = computed(() => [
  { text: 'Code Tarif', value: 'tariffCode' },
  { text: 'Tarif', value: 'tariffName' },
  { text: 'Code Catégorie Tariff', value: 'tariffCategoryCode' },
  { text: 'Valeur Tarif', value: 'productPrice' },
  { text: 'TVA', value: 'productTva', hidden: !isOfferTariff.value },
  { text: 'Selected', value: 'selected', hidden: !isOfferTariff.value }
]);

const fields = ref(
  Object.entries(props.rawContentTypes[props.field.type].fields).reduce(
    (prev, [k, v]) => ({ ...prev, [k]: { ...v, rules: [], dialog: false } }),
    {}
  )
);
const computedQuery = computed(() => {
  switch (store.getters.workspace?.settings?.horanetSource) {
    case 'postgres':
      return [
        getGraphqlSchema('alphaHoranetProduitVente'),
        {
          codeTerminal: store.getters.workspace?.settings?.codeTerminal
            ? parseInt(store.getters.workspace?.settings?.codeTerminal)
            : null,
          codeProduits: horanetProduct
            ? [parseInt(horanetProduct.horanetProductCode)]
            : null
        },
        { enabled }
      ];
    default:
      return [
        getGraphqlSchema('horanetProduitsVente'),
        {
          codeTerminal: store.getters.workspace?.settings?.codeTerminal,
          codeProduits: horanetProduct
            ? [`${horanetProduct.horanetProductCode}`]
            : null
        },
        { enabled }
      ];
  }
});

const { result, load, loading } = useLazyQuery(...computedQuery.value);

const tariffs = computed(() => {
  if (!loading.value) {
    if (
      result.value
      && result.value.horanetProduitsVente
      && result.value.horanetProduitsVente.length > 0
    ) {
      const computedTariffs = [];
      (result.value?.horanetProduitsVente || []).forEach(
        item => {
          const auxiliarTariff = { ...item };
          if (!auxiliarTariff.horanetProductTariff) {
            auxiliarTariff.horanetProductTariff = {
              tariffCode: item.tariffCode,
              tariffName: item.tariffName,
              horanetTariffCategoryCode: item.horanetTariffCategoryCode
            };
          }

          if (computedTariffs.find(t => t.codeTariff === auxiliarTariff.horanetProductTariff.tariffCode && t.tariffCategoryCode === auxiliarTariff.horanetProductTariff.horanetTariffCategoryCode)) return;

          let tariff = {
            ...auxiliarTariff,
            ...(auxiliarTariff?.horanetProductTariff || {}),
            tariffCode: auxiliarTariff.tariffCode ? auxiliarTariff.tariffCode.toString() : null,
            productTva: `${auxiliarTariff.horanetProductTva} %`,
            productPrice: `${parseFloat(auxiliarTariff.horanetProductPrice / 100).toFixed(
              2
            )} €`,
            tariffCategoryCode: auxiliarTariff.horanetProductTariff.horanetTariffCategoryCode
          };
          if (tariff.horanetProductTariff) {
            tariff.tariffCode = `${tariff.horanetProductTariff.tariffCode}`;
          }

          const tariffIndex = selectedTarifs.value.findIndex(
            t => t.codeTariff === tariff.tariffCode
          );

          if (tariffIndex !== -1) {
            tariff = {
              ...tariff,
              ...selectedTarifs.value[tariffIndex]
            };
          }
          computedTariffs.push(tariff);
        }
      );

      return computedTariffs;
    }
  }
  return [];
});

const clearAllTarifs = () => {
  selectedTarifs.value.forEach(t => {
    t.isActive = false;
  });
};

const selectAllTarifs = () => {
  selectedTarifs.value.forEach(t => {
    t.isActive = true;
  });
};

const updateField = (event, item, tariffField) => {
  const tariffIndex = selectedTarifs.value.findIndex(
    t => t.codeTariff === item.tariffCode
  );
  if (tariffIndex === -1) {
    selectedTarifs.value.push({
      codeTariff: item.tariffCode,
      description: item.description,
      isGymlibTariff: item.isGymlibTariff,
      negotiatedPrice: item.negotiatedPrice,
      tariffMaxAge: item.tariffMaxAge,
      defaultTariff: item.defaultTariff,
      isActive: item.isActive,
      [tariffField]: event
    });
  } else {
    selectedTarifs.value[tariffIndex] = {
      ...selectedTarifs.value[tariffIndex],
      [tariffField]: event
    };
  }
};

const addMissingTariffs = () => {
  tariffs.value.forEach(t => {
    if (!(props.value || []).find(p => p.codeTariff === t.tariffCode)) {
      selectedTarifs.value.push({
        codeTariff: t.tariffCode,
        description: t.description,
        isGymlibTariff: t.isGymlibTariff,
        negotiatedPrice: t.negotiatedPrice,
        tariffMaxAge: t.tariffMaxAge,
        defaultTariff: t.defaultTariff,
        isActive: false
      });
    }
  });
};

const isSimpleField = fieldType => {
  const simpleFields = ['Text', 'Number', 'Select', 'Slider', 'Textarea', 'Checkbox', 'Switch', 'Radio button'];
  return simpleFields.includes(fieldType.viewType);
};

watch(selectedTarifs, () => {
  emit('input', selectedTarifs.value);
}, { deep: true });

onBeforeMount(async () => {
  if (enabled.value) {
    await load();
    if (tariffs.value && !props.value) {
      selectAllTarifs();
    }

    const tariffsMap = new Set((props.value || []).map(tariff => tariff.codeTariff));
    if (tariffs.value.some(tariff => !tariffsMap.has(tariff.codeTariff))) {
      addMissingTariffs();
    }
  }

  Object.entries(fields.value).forEach(
    ([k, v]) => {
      if (v.type === 'String') {
        if (v?.maxCharacters) {
          fields.value[k].rules.push(
            f => (f?.length || 0) <= f.maxCharacters || `Max ${f.maxCharacters} characters`
          );
        }
      }

      fields.value[k].show = item => {
        if (fields.value[k].hide) return false;

        if (fields.value[k].hideWhen) {
          const hideRuleFields = Array.isArray(fields.value[k].hideWhen) ? fields.value[k].hideWhen : [fields.value[k].hideWhen];

          const showArray = hideRuleFields.map(hideRule => !eval(`"${item[hideRule.field]}"${hideRule.condition}"${hideRule.value}"`));

          return showArray.every(i => i);
        }

        return true;
      };
    }
  );
});
</script>
